import type React from 'react';
import { useCallback } from 'react';
import { hexToVec3 } from '../../utils';
import type { FormField } from '../../form-types';
import type { BaseShaderConfig } from '../../base-config';
import { TextureShader } from '../../texture-shader';
import {
  ShaderBehavior,
  ShaderCategory,
  type ShaderMetadata,
} from '../../shader-types';
import { useShaderPreview } from '../../hooks/useShaderPreview';

// 1. FireballShader Model
export type FireballShaderConfig = BaseShaderConfig & {
  name: 'fireball';
  baseColor: string;
  topColor: string;
  backgroundColor: string;
  intensity: number;
};

export class FireballShader extends TextureShader {
  name = 'fireball' as const;
  width!: number;
  height!: number;
  referenceWidth!: number;
  referenceHeight!: number;
  rotation!: number;
  baseColor!: string;
  topColor!: string;
  backgroundColor!: string;
  intensity!: number;

  constructor(props: Partial<FireballShader> = {}) {
    super();

    // Set defaults
    this.name = 'fireball';
    this.width = 512;
    this.height = 512;
    this.referenceWidth = 512;
    this.referenceHeight = 512;
    this.rotation = 0;
    this.baseColor = '#ff8800';
    this.topColor = '#ffff00';
    this.backgroundColor = '#000000';
    this.intensity = 1.0;

    // Apply provided props
    Object.assign(this, props);
  }

  generate(): string {
    return `
            precision mediump float;
            ${this.getTextureUniforms()}
            uniform vec3 u_base_color;
            uniform vec3 u_top_color;
            uniform vec3 u_background_color;
            uniform float u_intensity;

            float random(vec2 st) {
                return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
            }

            float noise(vec2 st) {
                vec2 i = floor(st);
                vec2 f = fract(st);
                float a = random(i);
                float b = random(i + vec2(1.0, 0.0));
                float c = random(i + vec2(0.0, 1.0));
                float d = random(i + vec2(1.0, 1.0));
                vec2 u = f * f * (3.0 - 2.0 * f);
                return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
            }

            float fbm(vec2 p) {
                float value = 0.0;
                float amplitude = 0.5;
                for(int i = 0; i < 6; i++) {
                    value += amplitude * noise(p);
                    p *= 2.0;
                    amplitude *= 0.5;
                }
                return value;
            }

            void main() {
                ${this.getTextureUVPreamble()}

                // If we're outside the reference bounds, show background
                if (isOutOfBounds) {
                    gl_FragColor = vec4(u_background_color, 1.0);
                    return;
                }

                // Convert to centered coordinates
                vec2 centered_uv = (uv - 0.5) * 2.0;
                float t = u_time;

                // Create fireball shape
                float dist = length(centered_uv);

                // Animated distortion for flame effect
                vec2 distortion = vec2(
                    fbm(centered_uv * 3.0 + t * 2.0),
                    fbm(centered_uv * 3.0 + t * 2.0 + 10.0)
                ) * 0.3;

                float distorted_dist = length(centered_uv + distortion);

                // Create fireball with intensity
                float fireball = 1.0 - smoothstep(0.3, 0.8, distorted_dist);
                fireball *= u_intensity;

                // Add inner core
                float core = 1.0 - smoothstep(0.1, 0.4, distorted_dist);

                // Color mixing based on distance and intensity
                vec3 color = u_background_color;
                if (fireball > 0.0) {
                    float temp = core * 0.7 + fireball * 0.3;
                    color = mix(u_base_color, u_top_color, temp);
                    color *= fireball;
                }

                // Add glow effect
                float glow = 1.0 - smoothstep(0.8, 1.2, distorted_dist);
                color += u_base_color * glow * 0.3;

                gl_FragColor = vec4(color, 1.0);
            }
        `;
  }

  static getFormConfig(): FormField[] {
    return [
      {
        type: 'range',
        name: 'intensity',
        label: 'Fire Intensity',
        description: 'Intensity and brightness of the fireball',
        min: 0.1,
        max: 3,
        step: 0.1,
        defaultValue: 1.0,
        colspan: 1,
      },
      {
        type: 'color',
        name: 'baseColor',
        label: 'Base Color',
        description: 'Core color of the fireball',
        defaultValue: '#ff8800',
        colspan: 1,
      },
      {
        type: 'color',
        name: 'topColor',
        label: 'Top Color',
        description: 'Outer flame color',
        defaultValue: '#ffff00',
        colspan: 1,
      },
      {
        type: 'color',
        name: 'backgroundColor',
        label: 'Background Color',
        description: 'Background color',
        defaultValue: '#000000',
        colspan: 2, // Full width for background
      },
    ];
  }

  static getMetadata(): ShaderMetadata {
    return {
      behavior: ShaderBehavior.TEXTURE,
      category: ShaderCategory.EFFECTS,
      description:
        'Intense fireball effect with customizable colors and intensity',
      tags: ['fireball', 'fire', 'explosion', 'effect', 'intense'],
    };
  }
}

// 2. FireballPreview Component
export const FireballPreview: React.FC<{ model: FireballShader }> = ({
  model,
}) => {
  const getUniformNames = useCallback(
    () => ['u_base_color', 'u_top_color', 'u_background_color', 'u_intensity'],
    []
  );

  const updateUniforms = useCallback(
    (
      gl: WebGLRenderingContext,
      locations: Record<string, WebGLUniformLocation | null>,
      model: FireballShader
    ) => {
      // Update shader-specific uniforms
      gl.uniform1f(locations.u_intensity, model.intensity);

      // Update color uniforms
      const baseColorVec3 = hexToVec3(model.baseColor);
      const topColorVec3 = hexToVec3(model.topColor);
      const backgroundColorVec3 = hexToVec3(model.backgroundColor);

      gl.uniform3fv(locations.u_base_color, baseColorVec3);
      gl.uniform3fv(locations.u_top_color, topColorVec3);
      gl.uniform3fv(locations.u_background_color, backgroundColorVec3);
    },
    []
  );

  const canvasRef = useShaderPreview(model, getUniformNames, updateUniforms);

  return <canvas ref={canvasRef} width={model.width} height={model.height} />;
};
